import { redirect, type LoaderFunctionArgs } from 'react-router-dom'
import type { RouteFactory } from '@/routing/types'

/**
 * Creates legacy redirect routes for the feature-first to matter-first URL migration.
 *
 * These redirects ensure backwards compatibility when the URL hierarchy changes.
 * They use loader-based 302 redirects with no business logic, data fetching, or guard evaluation.
 *
 * Routes without a matter context redirect to /dashboard/matters where users select a matter first.
 */
export const createLegacyRedirects: RouteFactory = () => [
  // Medical Chronology: /dashboard/medical-chronology/m/:matterId/* → /dashboard/m/:matterId/medical-chronology/*
  {
    path: '/dashboard/medical-chronology/m/:matterId/*',
    loader: ({ params }: LoaderFunctionArgs) => {
      const { matterId } = params
      const rest = params['*']
      const suffix = rest ? `/${rest}` : ''
      return redirect(`/dashboard/m/${matterId}/medical-chronology${suffix}`)
    },
  },

  // Bare medical-chronology without matter → redirect to matters table
  {
    path: '/dashboard/medical-chronology',
    loader: () => redirect('/dashboard/matters'),
  },

  // Medical Billing Summaries: /dashboard/medical-billing-summaries/m/:matterId/* → /dashboard/m/:matterId/billing-summaries/*
  {
    path: '/dashboard/medical-billing-summaries/m/:matterId/*',
    loader: ({ params }: LoaderFunctionArgs) => {
      const { matterId } = params
      const rest = params['*']
      const suffix = rest ? `/${rest}` : ''
      return redirect(`/dashboard/m/${matterId}/billing-summaries${suffix}`)
    },
  },

  // Bare medical-billing-summaries without matter → redirect to matters table
  {
    path: '/dashboard/medical-billing-summaries',
    loader: () => redirect('/dashboard/matters'),
  },

  // Chat with matter: /dashboard/chat/:feature/m/:matterId/:chatId? → /dashboard/m/:matterId/chat/:feature/:chatId?
  {
    path: '/dashboard/chat/:chatFeature/m/:matterId/:chatId?',
    loader: ({ params }: LoaderFunctionArgs) => {
      const { chatFeature, matterId, chatId } = params
      const suffix = chatId ? `/${chatId}` : ''
      return redirect(`/dashboard/m/${matterId}/chat/${chatFeature}${suffix}`)
    },
  },

  // Chat without matter (legacy): /dashboard/chat/:feature/:chatId → /dashboard/matters
  {
    path: '/dashboard/chat/:chatFeature/:chatId',
    loader: ({ params }: LoaderFunctionArgs) => {
      const { chatId } = params

      // Edge case: When chatId is 'm', the URL is actually the start of a matter path
      // e.g., /dashboard/chat/assistant/m/123/conv-456
      // The 'm' segment gets captured as :chatId, but the full URL should be handled
      // by the '/dashboard/chat/:chatFeature/m/:matterId/:chatId?' route above.
      // Returning null lets React Router continue matching to find the correct route.
      if (chatId === 'm') {
        return null
      }

      return redirect('/dashboard/matters')
    },
  },

  // Bare chat feature: /dashboard/chat/:feature → /dashboard/matters
  {
    path: '/dashboard/chat/:chatFeature',
    loader: () => redirect('/dashboard/matters'),
  },

  // Drives: /dashboard/drives/:driveName/* → /dashboard/matters
  {
    path: '/dashboard/drives/:driveName/*',
    loader: () => redirect('/dashboard/matters'),
  },

  // Bare drives: /dashboard/drives → /dashboard/matters
  {
    path: '/dashboard/drives',
    loader: () => redirect('/dashboard/matters'),
  },

  // Legacy files path: /dashboard/files → /dashboard/matters
  {
    path: '/dashboard/files',
    loader: () => redirect('/dashboard/matters'),
  },

  // User Files (new pipeline): /dashboard/user-files/m/:matterId/:folderId? → /dashboard/m/:matterId/user-files/:folderId?
  {
    path: '/dashboard/user-files/m/:matterId/:folderId?',
    loader: ({ params }: LoaderFunctionArgs) => {
      const { matterId, folderId } = params
      const suffix = folderId ? `/${folderId}` : ''
      return redirect(`/dashboard/m/${matterId}/user-files${suffix}`)
    },
  },

  // Bare user-files path without matter → redirect to matters table
  {
    path: '/dashboard/user-files',
    loader: () => redirect('/dashboard/matters'),
  },
]
